// Velocidad de crecimiento en talla (056A/056B, hoja de reverso: "Velocidad de Crecimiento (Talla), percentiles"). Método impreso en el
// papel: "se toma la talla en dos momentos diferentes, el intervalo entre las dos mediciones no debe ser menor a 3 ni mayor a 18 meses;
// se obtiene la diferencia en centímetros, que se divide para el número de meses transcurrido entre ellas, y el valor obtenido luego se
// multiplica por doce. El resultado obtenido se grafica en la mitad entre las dos mediciones."
//
// Referencia: tablas "Longitudinal Chicos/Chicas" (velocidad de crecimiento, cm/año) de la Fundación Faustino Orbegozo Eizaguirre, 2004
// (graficos/datos/velocidadTallaOrbegozo.json). La tabla publica P3, P25, P50 y P97; el papel dibuja además P10, P75 y P90, que no están en
// ninguna tabla: se leyeron de las curvas vectoriales del gráfico del mismo documento (calibradas con su grilla y validadas contra los
// cuatro percentiles de la tabla, ver `derivadosDeCurva` en el JSON) y se dibujan con trazo discontinuo. Puro, sin React (testeable sin DOM).
import type { Observation, Patient } from '@medplum/fhirtypes';
import type { PuntoSerie, Serie } from '../graficos/tipos';
import { PALETA_SERIES } from '../graficos/tema';
import datosOrbegozo from '../graficos/datos/velocidadTallaOrbegozo.json';
import { edadExactaEnDias } from '../lib/percentilesCrecimiento';
import type { CurvaPercentilDatos } from './curvaPercentilPorEdad';

const TALLA_LOINC = '8302-2';
const DIAS_POR_MES = 30.4375;
export const INTERVALO_MIN_MESES = 3;
export const INTERVALO_MAX_MESES = 18;

interface Medicion {
  readonly fecha: string;
  readonly cm: number;
}

function medicionesDeTalla(observaciones: readonly Observation[]): Medicion[] {
  return observaciones
    .filter((o) => o.code?.coding?.some((c) => c.system === 'http://loinc.org' && c.code === TALLA_LOINC))
    .filter((o): o is Observation & { effectiveDateTime: string; valueQuantity: { value: number } } => typeof o.effectiveDateTime === 'string' && typeof o.valueQuantity?.value === 'number')
    // Más reciente primero por `meta.lastUpdated`: al corregir una talla el mismo día (mismo instante) manda la corrección.
    .sort((a, b) => (b.meta?.lastUpdated ?? '').localeCompare(a.meta?.lastUpdated ?? ''))
    .map((o) => ({ fecha: o.effectiveDateTime, cm: o.valueQuantity.value }))
    .filter((m, i, todas) => todas.findIndex((x) => new Date(x.fecha).getTime() === new Date(m.fecha).getTime()) === i)
    .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
}

/**
 * Un punto por cada medición que tenga, más adelante, otra a 3-18 meses (la más cercana que cumpla el mínimo de 3 meses):
 * x = edad (días) en la mitad entre las dos mediciones, y = cm/año. Sin fecha de nacimiento no hay eje de edad.
 */
export function puntosVelocidadTalla(observaciones: readonly Observation[], nacimiento: string | undefined): PuntoSerie[] {
  if (!nacimiento) return [];
  const m = medicionesDeTalla(observaciones);
  const puntos: PuntoSerie[] = [];
  for (let i = 0; i < m.length; i++) {
    for (let j = i + 1; j < m.length; j++) {
      const meses = (new Date(m[j]!.fecha).getTime() - new Date(m[i]!.fecha).getTime()) / (1000 * 60 * 60 * 24 * DIAS_POR_MES);
      if (meses < INTERVALO_MIN_MESES) continue; // demasiado cerca: se prueba con la siguiente
      if (meses > INTERVALO_MAX_MESES) break; // ya se pasó del máximo: las siguientes están más lejos
      const cmPorAnio = ((m[j]!.cm - m[i]!.cm) / meses) * 12;
      const edadA = edadExactaEnDias(nacimiento, m[i]!.fecha);
      const edadB = edadExactaEnDias(nacimiento, m[j]!.fecha);
      puntos.push({ x: (edadA + edadB) / 2, y: Math.round(cmPorAnio * 100) / 100 });
      break;
    }
  }
  return puntos;
}

const DIAS_POR_ANIO = 365.25;
/** Edades (años) de la lámina de adolescentes que se dibujan de la referencia: 10 a 18. */
const REFERENCIA_DESDE_ANIOS = 10;
const REFERENCIA_HASTA_ANIOS = 18;
const COLUMNAS_REFERENCIA = [
  { indice: 1, id: 'p3', etiqueta: 'P3' },
  { indice: 2, id: 'p25', etiqueta: 'P25' },
  { indice: 3, id: 'p50', etiqueta: 'P50' },
  { indice: 4, id: 'p97', etiqueta: 'P97' },
] as const;
/** P10, P75 y P90: leídos de la curva impresa (columnas 1, 2 y 3 de `derivadosDeCurva`). */
const COLUMNAS_DERIVADAS = [
  { indice: 1, id: 'p10', etiqueta: 'P10' },
  { indice: 2, id: 'p75', etiqueta: 'P75' },
  { indice: 3, id: 'p90', etiqueta: 'P90' },
] as const;

/** Orden de las 7 líneas de la lámina, de abajo hacia arriba. */
const ORDEN_LINEAS = ['p3', 'p10', 'p25', 'p50', 'p75', 'p90', 'p97'];

/** Las 7 líneas de Orbegozo (10 a 18 años) para el sexo dado: P3, P25, P50, P97 de la tabla y P10, P75, P90 leídos de la curva (esas tres, con trazo discontinuo); vacío si el sexo no es hombre/mujer. */
export function lineasReferenciaVelocidadTalla(gender: Patient['gender']): Serie[] {
  if (gender !== 'male' && gender !== 'female') return [];
  const derivadas = (gender === 'male' ? datosOrbegozo.derivadosDeCurva.boys : datosOrbegozo.derivadosDeCurva.girls).filter((f) => f[0]! >= REFERENCIA_DESDE_ANIOS && f[0]! <= REFERENCIA_HASTA_ANIOS);
  // Las líneas de tabla terminan donde terminan las leídas de la curva: en niñas la tabla trae ceros artificiales en 17,5 y 18 años.
  const hasta = Math.max(...derivadas.map((f) => f[0]!));
  const filas = (gender === 'male' ? datosOrbegozo.boys : datosOrbegozo.girls).filter((f) => f[0]! >= REFERENCIA_DESDE_ANIOS && f[0]! <= hasta);
  const deTabla: Serie[] = COLUMNAS_REFERENCIA.map((c) => ({
    id: `vel-${c.id}`,
    etiqueta: c.etiqueta,
    color: 'var(--color-hce-muted)',
    trazo: 'punteado' as const,
    puntos: filas.map((f) => ({ x: f[0]! * DIAS_POR_ANIO, y: f[c.indice]! })),
  }));
  const deCurva: Serie[] = COLUMNAS_DERIVADAS.map((c) => ({
    id: `vel-${c.id}`,
    etiqueta: c.etiqueta,
    color: 'var(--color-hce-muted)',
    trazo: 'discontinuo' as const,
    puntos: derivadas.map((f) => ({ x: f[0]! * DIAS_POR_ANIO, y: f[c.indice]! })),
  }));
  return [...deTabla, ...deCurva].sort((a, b) => ORDEN_LINEAS.indexOf(a.id.slice(4)) - ORDEN_LINEAS.indexOf(b.id.slice(4)));
}

/** La curva del paciente con las líneas de referencia de Orbegozo, o `undefined` si no hay ninguna velocidad calculable. */
export function curvaVelocidadTallaDe(observaciones: readonly Observation[], patient: Pick<Patient, 'birthDate' | 'gender'>): CurvaPercentilDatos | undefined {
  const puntos = puntosVelocidadTalla(observaciones, patient.birthDate);
  if (puntos.length === 0) return undefined;
  const { color, trazo } = PALETA_SERIES[1];
  const serieReal: Serie = { id: 'velocidad-talla', etiqueta: 'Velocidad de crecimiento', color, trazo, unidad: 'cm/año', puntos };
  return { serieReal, lineasSd: lineasReferenciaVelocidadTalla(patient.gender) };
}
